import { apiClient } from './api-client';

// ==================== Interfaces ====================

export interface DashboardMetrics {
  tickets: {
    total: number;
    new: number;
    in_progress: number;
    pending: number;
    resolved: number;
    closed: number; // current status=closed (createdAt-scoped, role-scoped)
    backtracked: number;
    assigned: number; // created-in-range tickets that have an assignee (sums with unassigned to total)
    unassigned: number; // created-in-range tickets with no assignee
    resolvedInPeriod: number; // tickets resolved within the date range (by resolvedAt, includes archived)
  };
  performance: {
    avgFirstResponseTime: number;
    avgResolutionTime: number;
    firstResponseSlaCompliance: number;
    resolutionSlaCompliance: number;
    customerSatisfaction: number;
    aiResolutionRate: number;
  };
  volume: {
    today: number;
    yesterday: number;
    thisWeek: number;
    lastWeek: number;
    thisMonth: number;
    lastMonth: number;
  };
  channels: Record<string, number>; // created in range, excludes backtracked (legacy)
  channelsTotal?: Record<string, number>; // created in range per channel, INCLUDING backtracked (channel KPI cards)
  channelsOpen?: Record<string, number>; // created in range per channel still open (excludes resolved/closed/backtracked) — channel chart
  priorities: Record<string, number>;
  // Who completed the tickets — the admin-only "Resolved By" widget. Population
  // is COMPLETED tickets (resolvedAt OR closedAt) and follows the date picker, so
  // it does NOT reconcile with `tickets.resolved` above (current status, and
  // all-time on the Dashboard). Optional: a BFF build predating the field omits
  // it, and the widget must render its error state rather than "0% automated".
  resolutionSources?: { automation: number; agent: number; unknown: number };
}

export interface TimeSeriesData {
  period: string;
  created: number;
  resolved: number;
  responseTime?: number;
}

/** One row of Reports → Team Performance (GET /analytics/teams). */
export interface TeamPerformanceRow {
  teamId: string;
  name: string;
  parentTeamId: string | null;
  agentCount: number;
  ticketsAssigned: number;
  ticketsResolved: number;
  /** Percent; null when the team had no tickets. */
  slaCompliance: number | null;
  /** Minutes; null when nothing was responded to. */
  avgResponseTime: number | null;
}

export interface AgentMetrics {
  agentId: string;
  agentName: string;
  ticketsAssigned: number; // tickets the agent is the assignee of
  /** Tickets the agent is the assignee or a secondary agent of (older BFFs omit it). */
  ticketsWorkedOn?: number;
  ticketsResolved: number; // resolved tickets the agent worked on (shared with secondary agents)
  avgResponseTime: number | null; // minutes; null when no ticket has been responded to
  avgResolutionTime: number | null; // minutes; null when no ticket has been resolved
  slaCompliance: number;
  customerSatisfaction?: number;
  openTickets?: number; // current open ticket load (live snapshot, for Workload widget)
  role?: string; // roster sub-line on 13c; absent when the roster query degraded
}

export interface ChannelMetrics {
  [channel: string]: {
    volume: number;
    avgResponseTime: number;
  };
}

export interface SlaMetrics {
  firstResponse: {
    compliant: number;
    breached: number;
    percentage: number;
  };
  resolution: {
    compliant: number;
    breached: number;
    percentage: number;
  };
  byPriority: Record<string, { compliant: number; breached: number }>;
  // DISTINCT count of tickets that breached >= 1 SLA (not fr.breached + res.breached,
  // which double-counts tickets that breach both). Optional for backward compat.
  breachedTickets?: number;
}

export interface DateRange {
  start: string;
  end: string;
}

// ==================== Time-Series Interfaces ====================

export interface TimeSeriesDataPoint {
  time: string;
  avg: number | null;
  min: number | null;
  max: number | null;
  count: number;
}

export interface TimeSeriesResponse {
  metric_name: string;
  interval: string;
  data: TimeSeriesDataPoint[];
}

export interface PeriodComparison {
  metric_name: string;
  current_value: number;
  compare_value: number;
  change_percent: number;
  trend: 'up' | 'down' | 'flat';
}

/**
 * analytics-service answers compare/trends with nested period objects
 * (`current_period.avg` / `compare_period.avg`), not the flat values the
 * screens read. Flatten here so a missing number is 0 rather than undefined.
 */
function normalizePeriodComparison(raw: any): PeriodComparison {
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  return {
    metric_name: raw?.metric_name ?? '',
    current_value: num(raw?.current_value ?? raw?.current_period?.avg),
    compare_value: num(raw?.compare_value ?? raw?.compare_period?.avg),
    change_percent: num(raw?.change_percent),
    trend: raw?.trend === 'up' || raw?.trend === 'down' ? raw.trend : 'flat',
  };
}

export interface TrendAnalysis {
  metric_name: string;
  slope: number;
  r_squared: number;
  direction: 'up' | 'down' | 'flat';
  strength: string;
}

export interface Anomaly {
  time: string;
  value: number;
  expected: number;
  z_score: number;
  deviation: number;
}

// ==================== Reports Interfaces ====================

export interface ReportMetadata {
  metrics: Record<string, { name: string; description: string; type: string }[]>;
}

export interface ReportDimension {
  name: string;
  description: string;
}

export interface ChartTypeInfo {
  id: string;
  name: string;
  description: string;
}

export interface CustomReport {
  id: string;
  name: string;
  description?: string;
  data_source: string;
  metrics: string[];
  dimensions: string[];
  filters: Record<string, any>;
  time_range: { start: string; end: string } | null;
  chart_type: string;
  chart_config?: Record<string, any>;
  is_favorite: boolean;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  schedules?: ReportSchedule[];
}

export interface CreateReportRequest {
  name: string;
  description?: string;
  data_source: string;
  metrics: string[];
  dimensions?: string[];
  filters?: Record<string, any>;
  time_range?: { start: string; end: string };
  chart_type: string;
  /** Carries the builder's `TIME BUCKET` (day / week / month) — the spec has no
   *  first-class bucket field, and chart_config is where the chart's own
   *  rendering choices already live. */
  chart_config?: Record<string, any>;
}

/** The builder's draft, sent before the report exists. Same shape as
 *  CreateReportRequest minus the things only a saved report has. */
export interface PreviewReportRequest {
  data_source: string;
  metrics: string[];
  dimensions?: string[];
  filters?: Record<string, any>;
  time_range?: { start: string; end: string };
  chart_type: string;
  chart_config?: Record<string, any>;
}

export interface ReportPreviewResult {
  /** One number per bucket, in order — what the 104px preview chart draws. */
  series: number[];
  /** Rows the saved report would return. Stated, never estimated. */
  rows: number;
}

export interface UpdateReportRequest {
  name?: string;
  description?: string;
  metrics?: string[];
  dimensions?: string[];
  filters?: Record<string, any>;
  time_range?: { start: string; end: string };
  chart_type?: string;
  is_favorite?: boolean;
}

export interface ReportSchedule {
  id: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  delivery_type: 'email' | 'webhook';
  recipients: string[];
  format: 'pdf' | 'csv' | 'excel';
  timezone: string;
  is_active: boolean;
  next_run_at: string | null;
  last_sent_at: string | null;
}

export interface ScheduleReportRequest {
  frequency: 'daily' | 'weekly' | 'monthly';
  delivery_type: 'email' | 'webhook';
  recipients: string[];
  format: 'pdf' | 'csv' | 'excel';
  timezone?: string;
}

export interface ReportExecutionResult {
  report_id: string;
  // #18940 — the analytics run_report response the UI actually receives: one
  // entry per requested metric with a live scalar `value` over the window.
  report_name?: string;
  time_range?: { start?: string; end?: string; period_type?: string };
  results?: Array<{ metric: string; value?: number; data?: Record<string, any>[] }>;
  generated_at?: string;
  // Legacy/unused fields kept optional for back-compat.
  data?: Record<string, any>[];
  metadata?: Record<string, any>;
  executed_at?: string;
}

// Snapshots produced by workflow-service REPORT_GENERATION jobs that had
// "Show on Reports page" enabled. `metrics` is the payload computed by the
// analytics MetricsCollectorService — the modal re-renders the report from it.
export interface GeneratedReportSummary {
  id: string;
  report_type: string;
  title: string;
  window_days: number | null;
  source_job_id: string | null;
  source_job_name: string | null;
  generated_at: string;
}

export interface GeneratedReportDetail extends GeneratedReportSummary {
  metrics: Record<string, any>;
}

// ==================== Cross-Service Analytics Interfaces ====================

export interface CsatStats {
  total: number;
  completed: number;
  pending: number;
  expired: number;
  responseRate: number;
  avgRating: number;
  nps: number;
  ratingDistribution: Record<number, number>;
}

export interface ChatStats {
  waiting: number;
  active: number;
  endedToday: number;
}

export interface AutomationRuleStats {
  total: number;
  active: number;
}

export interface AutomationExecutionStats {
  total: number;
  completed: number;
  failed: number;
  pending: number;
  running: number;
}

export interface CustomerStatusCounts {
  [status: string]: number;
}

export interface SlaBreachStats {
  [key: string]: number | Record<string, number>;
}

export interface AiChatbotAnalytics {
  tenant_id: string;
  period_days: number;
  total_conversations: number;
  total_messages: number;
  total_handoffs: number;
  total_resolutions: number;
  handoff_rate: number;
  resolution_rate: number;
  avg_confidence: number;
  avg_sentiment: number;
  avg_response_time_ms: number;
  intent_distribution: Record<string, number>;
  handoff_reasons: Record<string, number>;
}

export interface PipelineInfo {
  id: string;
  name: string;
  stages: { id: string; name: string; sortOrder: number }[];
}

// ==================== Service ====================

class AnalyticsService {
  async getDashboardMetrics(
    dateRange?: DateRange,
    countsAllTime = false,
    teamId?: string,
  ): Promise<DashboardMetrics> {
    const params = new URLSearchParams();
    if (teamId) params.append('teamId', teamId);
    if (dateRange) {
      params.append('startDate', dateRange.start);
      params.append('endDate', dateRange.end);
    }
    // Dashboard sets this so Total/Status/Priority/Channel are all-time; the
    // Reports page omits it to keep those date-scoped.
    if (countsAllTime) params.append('countsAllTime', 'true');
    const queryString = params.toString();
    const response = await apiClient.get<DashboardMetrics>(
      `/analytics/dashboard${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  }

  async getTicketTrends(
    period: 'day' | 'week' | 'month',
    dateRange: DateRange,
    teamId?: string,
  ): Promise<TimeSeriesData[]> {
    const team = teamId ? `&teamId=${encodeURIComponent(teamId)}` : '';
    const response = await apiClient.get<TimeSeriesData[]>(
      `/analytics/tickets/trends?period=${period}&startDate=${dateRange.start}&endDate=${dateRange.end}${team}`
    );
    return response.data;
  }

  async getAgentMetrics(
    dateRange?: DateRange,
    opts?: { reportView?: boolean; teamId?: string },
  ): Promise<AgentMetrics[]> {
    const params = new URLSearchParams();
    if (opts?.teamId) params.append('teamId', opts.teamId);
    if (dateRange) {
      params.append('startDate', dateRange.start);
      params.append('endDate', dateRange.end);
    }
    // reportView is set only by the Reports → Agent Performance view and switches
    // the backend to the full-roster list (every org member incl. admins/supervisors).
    if (opts?.reportView) {
      params.append('view', 'report');
    }
    const queryString = params.toString();
    const response = await apiClient.get<AgentMetrics[]>(
      `/analytics/agents${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  }

  /** Reports → Team Performance: every team the caller may see, for the period. */
  async getTeamPerformance(dateRange?: DateRange): Promise<TeamPerformanceRow[]> {
    const params = new URLSearchParams();
    if (dateRange) {
      params.append('startDate', dateRange.start);
      params.append('endDate', dateRange.end);
    }
    const queryString = params.toString();
    const response = await apiClient.get<TeamPerformanceRow[]>(
      `/analytics/teams${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  }

  async getChannelMetrics(dateRange?: DateRange): Promise<ChannelMetrics> {
    const params = new URLSearchParams();
    if (dateRange) {
      params.append('startDate', dateRange.start);
      params.append('endDate', dateRange.end);
    }
    const queryString = params.toString();
    const response = await apiClient.get<ChannelMetrics>(
      `/analytics/channels${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  }

  async getSlaMetrics(dateRange?: DateRange, teamId?: string): Promise<SlaMetrics> {
    const params = new URLSearchParams();
    if (teamId) params.append('teamId', teamId);
    if (dateRange) {
      params.append('startDate', dateRange.start);
      params.append('endDate', dateRange.end);
    }
    const queryString = params.toString();
    const response = await apiClient.get<SlaMetrics>(
      `/analytics/sla${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  }

  // ==================== Time-Series ====================

  async getTimeSeries(
    metricName: string,
    options?: { startDate?: string; endDate?: string; interval?: string },
  ): Promise<TimeSeriesResponse> {
    const params = new URLSearchParams();
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    if (options?.interval) params.append('interval', options.interval);
    const qs = params.toString();
    const response = await apiClient.get<TimeSeriesResponse>(
      `/analytics/time-series/metrics/${metricName}${qs ? `?${qs}` : ''}`,
    );
    return response.data;
  }

  async comparePeriods(
    metricName: string,
    currentRange: DateRange,
    compareRange: DateRange,
  ): Promise<PeriodComparison> {
    const params = new URLSearchParams({
      currentStart: currentRange.start,
      currentEnd: currentRange.end,
      compareStart: compareRange.start,
      compareEnd: compareRange.end,
    });
    const response = await apiClient.get(
      `/analytics/time-series/compare/${metricName}?${params.toString()}`,
    );
    return normalizePeriodComparison(response.data);
  }

  async getTrends(metrics: string[], days?: number): Promise<PeriodComparison[]> {
    const params = new URLSearchParams({ metrics: metrics.join(',') });
    if (days) params.append('days', String(days));
    const response = await apiClient.get(
      `/analytics/time-series/trends?${params.toString()}`,
    );
    return Array.isArray(response.data) ? response.data.map(normalizePeriodComparison) : [];
  }

  async analyzeTrend(
    metricName: string,
    options?: { periodType?: string; lookbackPeriods?: number },
  ): Promise<TrendAnalysis> {
    const params = new URLSearchParams();
    if (options?.periodType) params.append('periodType', options.periodType);
    if (options?.lookbackPeriods) params.append('lookbackPeriods', String(options.lookbackPeriods));
    const qs = params.toString();
    const response = await apiClient.get<TrendAnalysis>(
      `/analytics/time-series/analyze/${metricName}${qs ? `?${qs}` : ''}`,
    );
    return response.data;
  }

  async detectAnomalies(
    metricName: string,
    options?: { thresholdStddev?: number; lookbackDays?: number },
  ): Promise<Anomaly[]> {
    const params = new URLSearchParams();
    if (options?.thresholdStddev) params.append('thresholdStddev', String(options.thresholdStddev));
    if (options?.lookbackDays) params.append('lookbackDays', String(options.lookbackDays));
    const qs = params.toString();
    const response = await apiClient.get<Anomaly[]>(
      `/analytics/time-series/anomalies/${metricName}${qs ? `?${qs}` : ''}`,
    );
    return response.data;
  }

  // ==================== Custom Reports ====================

  async getReportMetrics(dataSource?: string): Promise<any> {
    const path = dataSource
      ? `/analytics/reports/metadata/metrics?dataSource=${dataSource}`
      : '/analytics/reports/metadata/metrics';
    const response = await apiClient.get(path);
    return response.data;
  }

  async getReportDimensions(): Promise<ReportDimension[]> {
    const response = await apiClient.get<ReportDimension[]>('/analytics/reports/metadata/dimensions');
    return response.data;
  }

  async getChartTypes(): Promise<ChartTypeInfo[]> {
    const response = await apiClient.get<ChartTypeInfo[]>('/analytics/reports/metadata/chart-types');
    return response.data;
  }

  async listReports(includePublic?: boolean): Promise<CustomReport[]> {
    const params = new URLSearchParams();
    if (includePublic !== undefined) params.append('includePublic', String(includePublic));
    const qs = params.toString();
    const response = await apiClient.get<CustomReport[]>(
      `/analytics/reports${qs ? `?${qs}` : ''}`,
    );
    return response.data;
  }

  async getReport(reportId: string): Promise<CustomReport> {
    const response = await apiClient.get<CustomReport>(`/analytics/reports/${reportId}`);
    return response.data;
  }

  async createReport(data: CreateReportRequest): Promise<CustomReport> {
    const response = await apiClient.post<CustomReport>('/analytics/reports', data);
    return response.data;
  }

  async updateReport(reportId: string, data: UpdateReportRequest): Promise<CustomReport> {
    const response = await apiClient.put<CustomReport>(`/analytics/reports/${reportId}`, data);
    return response.data;
  }

  async deleteReport(reportId: string): Promise<void> {
    await apiClient.delete(`/analytics/reports/${reportId}`);
  }

  async runReport(
    reportId: string,
    dateRange?: DateRange,
  ): Promise<ReportExecutionResult> {
    const params = new URLSearchParams();
    if (dateRange) {
      params.append('startDate', dateRange.start);
      params.append('endDate', dateRange.end);
    }
    const qs = params.toString();
    const response = await apiClient.post<ReportExecutionResult>(
      `/analytics/reports/${reportId}/run${qs ? `?${qs}` : ''}`,
      {},
    );
    return response.data;
  }

  /**
   * Run an UNSAVED report spec and return the rows it would produce — what the
   * builder's live preview shows (phase 21 §4).
   *
   * The endpoint does not exist yet: `POST /analytics/reports/{id}/run` needs a
   * persisted report, and the builder is asking the question before there is
   * one. Until `POST /analytics/reports/preview` lands in analytics-service the
   * call 404s, and the caller renders the absence rather than a chart of
   * nothing — see ReportBuilder's `previewState`.
   */
  async previewReport(spec: PreviewReportRequest): Promise<ReportPreviewResult> {
    const response = await apiClient.post<ReportPreviewResult>(
      '/analytics/reports/preview',
      spec,
    );
    return response.data;
  }

  async scheduleReport(reportId: string, data: ScheduleReportRequest): Promise<ReportSchedule> {
    const response = await apiClient.post<ReportSchedule>(
      `/analytics/reports/${reportId}/schedule`,
      data,
    );
    return response.data;
  }

  async duplicateReport(reportId: string, name: string): Promise<CustomReport> {
    const response = await apiClient.post<CustomReport>(
      `/analytics/reports/${reportId}/duplicate?name=${encodeURIComponent(name)}`,
      {},
    );
    return response.data;
  }

  async exportReport(
    reportId: string,
    format: 'pdf' | 'csv' | 'excel',
    dateRange?: DateRange,
  ): Promise<Blob> {
    const params = new URLSearchParams({ format });
    if (dateRange) {
      params.append('startDate', dateRange.start);
      params.append('endDate', dateRange.end);
    }
    const response = await apiClient.post(
      `/analytics/reports/${reportId}/export?${params.toString()}`,
      {},
      { responseType: 'blob' },
    );
    return response.data;
  }

  // ==================== Templates ====================

  async listTemplates(category?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    const qs = params.toString();
    const response = await apiClient.get<any[]>(
      `/analytics/reports/templates${qs ? `?${qs}` : ''}`,
    );
    return response.data;
  }

  async createFromTemplate(templateId: string, name: string): Promise<CustomReport> {
    const response = await apiClient.post<CustomReport>(
      `/analytics/reports/templates/${templateId}/create?name=${encodeURIComponent(name)}`,
      {},
    );
    return response.data;
  }

  // ==================== Generated (scheduler) report snapshots ====================

  async listGeneratedReports(reportType?: string, limit?: number): Promise<GeneratedReportSummary[]> {
    const params = new URLSearchParams();
    if (reportType) params.append('reportType', reportType);
    if (limit) params.append('limit', String(limit));
    const qs = params.toString();
    const response = await apiClient.get<GeneratedReportSummary[]>(
      `/analytics/reports/generated${qs ? `?${qs}` : ''}`,
    );
    return response.data;
  }

  async getGeneratedReport(id: string): Promise<GeneratedReportDetail> {
    const response = await apiClient.get<GeneratedReportDetail>(
      `/analytics/reports/generated/${id}`,
    );
    return response.data;
  }

  // ==================== Cross-Service Analytics ====================

  async getCsatStats(dateRange?: DateRange): Promise<CsatStats> {
    const params = new URLSearchParams();
    if (dateRange) {
      params.append('startDate', dateRange.start);
      params.append('endDate', dateRange.end);
    }
    const qs = params.toString();
    const response = await apiClient.get<CsatStats>(
      `/analytics/cross-service/csat${qs ? `?${qs}` : ''}`,
    );
    return response.data;
  }

  async getChatStats(): Promise<ChatStats> {
    const response = await apiClient.get<ChatStats>('/analytics/cross-service/chat');
    return response.data;
  }

  async getAutomationRuleStats(): Promise<AutomationRuleStats> {
    const response = await apiClient.get<AutomationRuleStats>(
      '/analytics/cross-service/automations/rules',
    );
    return response.data;
  }

  async getAutomationExecutionStats(): Promise<AutomationExecutionStats> {
    const response = await apiClient.get<AutomationExecutionStats>(
      '/analytics/cross-service/automations/executions',
    );
    return response.data;
  }

  async getCustomerStatusCounts(): Promise<CustomerStatusCounts> {
    const response = await apiClient.get<CustomerStatusCounts>(
      '/analytics/cross-service/customers',
    );
    return response.data;
  }

  async getSlaBreachStats(dateRange?: DateRange): Promise<SlaBreachStats> {
    const params = new URLSearchParams();
    if (dateRange) {
      params.append('startDate', dateRange.start);
      params.append('endDate', dateRange.end);
    }
    const qs = params.toString();
    const response = await apiClient.get<SlaBreachStats>(
      `/analytics/cross-service/sla/breaches${qs ? `?${qs}` : ''}`,
    );
    return response.data;
  }

  async getAiChatbotAnalytics(days?: number): Promise<AiChatbotAnalytics> {
    const params = days ? `?days=${days}` : '';
    const response = await apiClient.get<AiChatbotAnalytics>(
      `/analytics/cross-service/ai${params}`,
    );
    return response.data;
  }

  // ==================== Pipeline Analytics ====================

  async getPipelines(): Promise<PipelineInfo[]> {
    const response = await apiClient.get<PipelineInfo[]>('/tickets/pipelines');
    return response.data;
  }

  // ==================== Helpers ====================

  formatDuration(minutes: number): string {
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }

  formatPercentage(value: number): string {
    return `${Math.round(value * 100) / 100}%`;
  }

  getPercentageChange(current: number, previous: number): number {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  }

  getSlaComplianceColor(percentage: number): string {
    if (percentage >= 95) return 'text-green-600';
    if (percentage >= 80) return 'text-yellow-600';
    return 'text-red-600';
  }

  getChannelIcon(channel: string): string {
    const icons: Record<string, string> = {
      email: '📧',
      chat: '💬',
      phone: '📞',
      whatsapp: '📱',
      social: '🌐',
      web: '🖥️',
      api: '🔌',
    };
    return icons[channel.toLowerCase()] || '📋';
  }

  getDateRangePresets(): { label: string; range: DateRange }[] {
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    const weekStart = new Date(now);
    weekStart.setDate(weekStart.getDate() - 7);

    const monthStart = new Date(now);
    monthStart.setMonth(monthStart.getMonth() - 1);

    const quarterStart = new Date(now);
    quarterStart.setMonth(quarterStart.getMonth() - 3);

    return [
      {
        label: 'Today',
        range: { start: today, end: today },
      },
      {
        label: 'Last 7 Days',
        range: { start: weekStart.toISOString().split('T')[0], end: today },
      },
      {
        label: 'Last 30 Days',
        range: { start: monthStart.toISOString().split('T')[0], end: today },
      },
      {
        label: 'Last 90 Days',
        range: { start: quarterStart.toISOString().split('T')[0], end: today },
      },
    ];
  }

  // ==================== AI Analytics ====================

  async getDashboardInsights(
    metrics: DashboardMetrics,
    previousMetrics?: DashboardMetrics,
  ): Promise<InsightsResponse> {
    const response = await apiClient.post<InsightsResponse>(
      '/analytics/ai/insights/dashboard',
      { metrics, previous_metrics: previousMetrics },
    );
    return response.data;
  }

  async explainMetric(
    metricName: string,
    currentValue: number,
    previousValue?: number,
  ): Promise<MetricExplanation> {
    const params = new URLSearchParams();
    params.append('metric_name', metricName);
    params.append('current_value', String(currentValue));
    if (previousValue !== undefined) params.append('previous_value', String(previousValue));
    const response = await apiClient.post<MetricExplanation>(
      `/analytics/ai/insights/explain?${params.toString()}`,
      null,
    );
    return response.data;
  }

  async detectAnomaliesAi(
    timeSeries: Array<{ period: string; value: number }>,
    metricName?: string,
  ): Promise<AnomalyDetectionResult> {
    const params = new URLSearchParams();
    if (metricName) params.append('metric_name', metricName);
    const response = await apiClient.post<AnomalyDetectionResult>(
      `/analytics/ai/insights/anomalies?${params.toString()}`,
      timeSeries,
    );
    return response.data;
  }

  async naturalLanguageQuery(
    query: string,
    opts?: { sessionId?: string; refresh?: boolean },
  ): Promise<NLQueryResponse> {
    const params = new URLSearchParams();
    if (opts?.sessionId) params.append('session_id', opts.sessionId);
    if (opts?.refresh) params.append('refresh', '1');
    const url = params.toString()
      ? `/analytics/ai/insights/nl-query?${params.toString()}`
      : '/analytics/ai/insights/nl-query';
    const response = await apiClient.post<NLQueryResponse>(url, { query });
    return response.data;
  }

  async nlQueryFeedback(params: {
    query: string;
    intent?: string;
    rating: 'up' | 'down';
    comment?: string;
  }): Promise<{ ok: boolean }> {
    const response = await apiClient.post<{ ok: boolean }>(
      '/analytics/ai/insights/nl-query/feedback',
      params,
    );
    return response.data;
  }

  async predictVolume(
    history: TimeSeriesData[],
    horizonDays: number = 7,
  ): Promise<VolumeForecast> {
    const params = new URLSearchParams();
    params.append('horizon_days', String(horizonDays));
    const response = await apiClient.post<VolumeForecast>(
      `/analytics/ai/predictions/volume?${params.toString()}`,
      history,
    );
    return response.data;
  }

  async predictSlaBreaches(
    slaData: any,
    volumeForecast?: any[],
  ): Promise<SlaBreachPrediction> {
    const response = await apiClient.post<SlaBreachPrediction>(
      '/analytics/ai/predictions/sla-breaches',
      { sla_data: slaData, volume_forecast: volumeForecast },
    );
    return response.data;
  }

  async predictWorkload(
    agentData: any[],
    volumeForecast?: any[],
  ): Promise<WorkloadPrediction> {
    const response = await apiClient.post<WorkloadPrediction>(
      '/analytics/ai/predictions/workload',
      { agent_data: agentData, volume_forecast: volumeForecast },
    );
    return response.data;
  }

  // ==================== Health Scores ====================

  async getCustomerHealth(limit?: number): Promise<CustomerHealthResponse> {
    const params = limit ? `?limit=${limit}` : '';
    const response = await apiClient.get<CustomerHealthResponse>(
      `/analytics/health-scores/customers${params}`,
    );
    return response.data;
  }

  async getAgentWellness(agentMetrics: AgentMetrics[]): Promise<AgentWellnessResponse> {
    const response = await apiClient.post<AgentWellnessResponse>(
      '/analytics/health-scores/agents',
      agentMetrics,
    );
    return response.data;
  }

  async getCostAnalytics(
    metrics: DashboardMetrics,
    aiAnalytics?: any,
  ): Promise<CostAnalyticsResponse> {
    const response = await apiClient.post<CostAnalyticsResponse>(
      '/analytics/health-scores/cost-analytics',
      { metrics, ai_analytics: aiAnalytics },
    );
    return response.data;
  }

  async getActivityHeatmap(): Promise<ActivityHeatmapResponse> {
    const response = await apiClient.get<ActivityHeatmapResponse>(
      '/analytics/health-scores/activity-heatmap',
    );
    return response.data;
  }

  async getDeflectionFunnel(
    metrics: DashboardMetrics,
    aiAnalytics?: any,
    kbAnalytics?: any,
  ): Promise<DeflectionFunnelResponse> {
    const response = await apiClient.post<DeflectionFunnelResponse>(
      '/analytics/health-scores/deflection-funnel',
      { metrics, ai_analytics: aiAnalytics, kb_analytics: kbAnalytics },
    );
    return response.data;
  }

  async getRecentActivity(): Promise<RecentActivity[]> {
    const response = await apiClient.get<RecentActivity[]>(
      '/analytics/recent-activity',
    );
    return response.data;
  }
}

// ==================== AI Analytics Interfaces ====================

export interface InsightsResponse {
  summary: string;
  highlights: string[];
  recommendations: string[];
  rootCauseChain: string[];
  generatedAt: string;
}

export interface MetricExplanation {
  metric: string;
  value: number;
  changePct: number | null;
  explanation: string;
  generatedAt: string;
}

export interface AnomalyDetectionResult {
  metric: string;
  anomalies: Array<{
    period: string;
    value: number;
    expected: number;
    zScore: number;
    direction: 'above' | 'below';
    severity: 'high' | 'medium';
  }>;
  stats: { mean: number; stdDev: number; dataPoints: number };
  explanation: string | null;
  detectedAt: string;
}

export interface NLQueryDrillDown {
  filters: Record<string, any>;
}

export interface NLQueryMetric {
  label: string;
  value: string | number;
  change?: { value: number; direction: 'up' | 'down' };
  drillDown?: NLQueryDrillDown;
}

export interface NLQueryChartPoint {
  name?: string;
  date?: string;
  value?: number | string;
  drillDown?: NLQueryDrillDown;
  [key: string]: any;
}

export interface NLQueryChart {
  type: 'line' | 'bar' | 'area' | 'pie' | 'donut';
  title?: string;
  data: NLQueryChartPoint[];
  config?: {
    xKey?: string;
    yKeys?: string[];
    colors?: string[];
  };
}

export interface NLQueryResponse {
  query: string;
  intent: {
    type: string;
    metric?: string;
    confidence?: number;
    modifiers?: string[];
    suggestedReport?: string;
    filters?: Record<string, any>;
    rawQuery?: string;
  };
  answer: string;
  metrics?: NLQueryMetric[];
  chart?: NLQueryChart | null;
  cacheInfo?: { hit: boolean; key?: string };
  suggestedReport: string | null;
  suggestedFilters: Record<string, any>;
  generatedAt: string;
}

export interface VolumeForecast {
  forecast: Array<{
    period: string;
    predicted: number;
    lower: number;
    upper: number;
  }>;
  confidence: number;
  trend: 'increasing' | 'decreasing' | 'stable';
  slope: number;
  horizonDays: number;
  basedOnDataPoints: number;
  message?: string;
  generatedAt: string;
}

export interface SlaBreachPrediction {
  predictions: Array<{
    period: string;
    predictedVolume: number;
    estimatedFirstResponseBreaches: number;
    estimatedResolutionBreaches: number;
    totalEstimatedBreaches: number;
  }>;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  totalPredictedBreaches: number;
  currentCompliance: { firstResponse: number; resolution: number };
  recommendations: string[];
  generatedAt: string;
}

export interface WorkloadPrediction {
  agents: Array<{
    name: string;
    currentLoad: number;
    predictedLoad: number;
    utilization: number;
    status: 'overloaded' | 'optimal' | 'underutilized';
  }>;
  summary: {
    totalAgents: number;
    avgLoadPerAgent: number;
    predictedDailyVolume: number;
    overloaded: number;
    underutilized: number;
  };
  recommendations: string[];
  generatedAt: string;
}

// ==================== Health Score Interfaces ====================

export interface CustomerHealthItem {
  id: string;
  name: string;
  email: string;
  organization: string;
  score: number;
  status: 'healthy' | 'needs_attention' | 'at_risk';
  signals: Record<string, number>;
  riskFactors: string[];
  openTickets: number;
  totalTickets: number;
}

export interface CustomerHealthResponse {
  customers: CustomerHealthItem[];
  summary: {
    total: number;
    healthy: number;
    needsAttention: number;
    atRisk: number;
    avgScore: number;
  };
  generatedAt: string;
}

export interface AgentWellnessItem {
  agentId: string;
  name: string;
  wellnessScore: number;
  burnoutRisk: 'healthy' | 'warning' | 'elevated';
  signals: Record<string, number>;
  riskFactors: string[];
  ticketsAssigned: number;
  ticketsResolved: number;
  slaCompliance: number;
  satisfaction: number;
}

export interface AgentWellnessResponse {
  agents: AgentWellnessItem[];
  summary: {
    total: number;
    healthy: number;
    warning: number;
    elevated: number;
    avgWellness: number;
  };
  generatedAt: string;
}

export interface CostAnalyticsResponse {
  totalCost: number;
  avgCostPerTicket: number;
  totalResolved: number;
  byChannel: Array<{
    channel: string;
    tickets: number;
    costPerTicket: number;
    totalCost: number;
  }>;
  aiSavings: {
    aiResolvedTickets: number;
    estimatedSavings: number;
    avgHumanCost: number;
    aiCostPerTicket: number;
    savingsPercentage: number;
  } | null;
  generatedAt: string;
}

export interface ActivityHeatmapResponse {
  rows: Array<{
    day: string;
    hours: number[];
    total: number;
  }>;
  peakDay: string;
  peakHour: number;
  peakValue: number;
  totalTickets: number;
  generatedAt: string;
}

export interface DeflectionFunnelResponse {
  stages: Array<{
    stage: string;
    count: number;
    percentage: number;
  }>;
  deflectionRate: number;
  aiContainmentRate: number;
  kbDeflectionRate: number;
  totalDeflected: number;
  generatedAt: string;
}

export interface RecentActivity {
  id: string;
  type: 'ticket_created' | 'ticket_resolved' | 'ticket_assigned' | 'message_received' | 'sla_warning' | 'sla_breach';
  title: string;
  description: string;
  timestamp: string;
  meta?: {
    ticketId?: string;
    agentName?: string;
    customerName?: string;
  };
}

export const analyticsService = new AnalyticsService();
export default analyticsService;
